import { type HttpClient } from '../http';

import { type Presence } from './presence.types';

/**
 * Who is around: household and accepted friends, in one snapshot.
 *
 * Warning: A **snapshot**, not a subscription. The server keeps it live on a
 * `/presence` WebSocket namespace, which this package deliberately does not
 * reach — it is HTTP and declares no runtime. Read it once on arrival; wire
 * the socket in the application if the screen has to keep up.
 *
 * @param http Transport supplied by the application.
 * @returns The `presence` domain of the API client.
 */
export function createPresenceApi(http: HttpClient) {
  return {
    /**
     * Reads the presence of everyone the caller can see.
     *
     * Warning: The audience is household **and** friends, in one list with
     * nothing to tell them apart. A screen that means "the household" has to
     * intersect it with the family's own members.
     *
     * @returns One entry per person, in no guaranteed order.
     */
    listAudience: () => http.get<Presence[]>('/presence/friends'),

    /**
     * Says the caller is around.
     *
     * Warning: what a client with no socket has instead of one. Presence is
     * kept live on a WebSocket namespace this package does not reach, and
     * without a beat nobody is ever seen online — the reader included.
     *
     * Meant to be called on arrival and every couple of minutes while the
     * screen is visible; the server forgets somebody after five minutes of
     * silence.
     *
     * @returns Nothing.
     */
    heartbeat: () => http.post<void>('/presence/heartbeat', {}),
  };
}

export type PresenceApi = ReturnType<typeof createPresenceApi>;
